<template>
  <field-frame :field="field" :show-optional="showOptional">
    <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <div v-for="(photo, idx) in field.photos" :key="idx" class="relative aspect-square bg-q4cl-surface rounded-q4cl overflow-hidden border border-q4cl-border">
        <img v-if="photo.image && photo.image.blobUrl" :src="photo.image.blobUrl" class="w-full h-full object-cover" :alt="$t('撮影した写真')" />
        <button class="absolute top-1 right-1 w-6 h-6 rounded-full bg-rose-500 text-white text-xs leading-none" @click="removePhoto(idx)" type="button" :aria-label="$t('この写真を削除')"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
        <button class="absolute bottom-1 right-1 w-6 h-6 rounded-full bg-q4cl-primary text-white text-xs leading-none" @click="rotatePhoto(idx)" type="button" :aria-label="$t('この写真を回転')"><i class="fa-solid fa-rotate-right" aria-hidden="true"></i></button>
      </div>
      <div v-if="canAddMore" class="aspect-square rounded-q4cl border-2 border-dashed border-q4cl-border bg-white overflow-hidden flex flex-col">
        <button type="button" class="flex-1 flex flex-col items-center justify-center gap-1 text-q4cl-muted text-xs hover:bg-q4cl-surface" @click="triggerCamera" :aria-label="$t('写真を撮影')">
          <i class="fa-solid fa-camera text-xl" aria-hidden="true"></i>
          <span>{{ $t('写真を撮影') }}</span>
        </button>
        <div class="border-t border-dashed border-q4cl-border" aria-hidden="true"></div>
        <button type="button" class="flex-1 flex flex-col items-center justify-center gap-1 text-q4cl-muted text-xs hover:bg-q4cl-surface" @click="triggerLibrary" :aria-label="$t('写真を選択')">
          <i class="fa-solid fa-image text-xl" aria-hidden="true"></i>
          <span>{{ $t('写真を選択') }}</span>
        </button>
      </div>
    </div>
    <input ref="cameraInput" type="file" accept="image/*" capture="environment" class="hidden" @change="onFileSelected" />
    <input ref="libraryInput" type="file" accept="image/*" class="hidden" @change="onFileSelected" />
  </field-frame>
</template>

<script>
import FieldFrame from '@/components/ui/FieldFrame.vue'

async function loadImage(src) {
  return new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });
}

/** Blob を DataURL 文字列 (data:image/jpeg;base64,...) に変換 */
function blobToDataUrl(blob) {
  return new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = () => res(reader.result);
    reader.onerror = () => rej(reader.error || new Error('FileReader failed'));
    reader.readAsDataURL(blob);
  });
}

/** canvas から blobUrl (表示用) と dataUrl (暗号化用) を生成。
 *  dataUrl は toBlob で得た同じ Blob から導出し、表示画像と保存画像を同一バイトに揃える
 *  (canvas を二度エンコードしない)。 保存形式は console 互換のため data:image/jpeg;base64,... のまま。 */
async function canvasToBlobAndDataUrl(canvas) {
  const blob = await new Promise((res, rej) =>
    canvas.toBlob(b => (b ? res(b) : rej(new Error('canvas.toBlob returned null'))), 'image/jpeg', 0.85)
  );
  const blobUrl = URL.createObjectURL(blob);
  const dataUrl = await blobToDataUrl(blob);
  return { blobUrl, dataUrl };
}

export default {
  name: 'PhotoField',
  components: {
    'field-frame': FieldFrame
  },
  props: {
    field: { type: Object, required: true },
    showOptional: { type: Boolean, default: false }
  },
  setup(props) {
    const cameraInput = Vue.ref(null);
    const libraryInput = Vue.ref(null);
    if (!Array.isArray(props.field.photos)) props.field.photos = [];

    const canAddMore = Vue.computed(() => {
      const max = props.field.maxPhotos || 4;
      return props.field.photos.length < max;
    });

    function triggerCamera() {
      cameraInput.value?.click();
    }

    function triggerLibrary() {
      libraryInput.value?.click();
    }

    async function onFileSelected(e) {
      const input = e.target;
      const file = input.files && input.files[0];
      if (!file) return;
      const enc = window.encryption;
      const maxSize = props.field.maxSize || 1024;

      try {
        // EXIF Orientation を適用しつつデコード時にダウンスケール (メモリ節約)。
        // resizeWidth/Height を両方指定するとアスペクト比が崩れるため幅のみ指定する。
        // imageOrientation / resize 非対応ブラウザ (旧 iOS Safari 等) はフォールバック。
        let bitmap;
        try {
          bitmap = await createImageBitmap(file, {
            imageOrientation: 'from-image',
            resizeWidth: maxSize,
            resizeQuality: 'high'
          });
        } catch (_) {
          bitmap = await createImageBitmap(file);
        }

        // 長辺を maxSize に収めつつ縦横比を保持。scale は 1 以下に clamp。
        // 小さい画像も含め常に canvas で再ラスタライズ → 悪意ある画像のサニタイズを一律に効かせる
        // (元 File のバイト列を素通しで暗号化・送信する経路は作らない)。
        const scale = Math.min(1, maxSize / bitmap.width, maxSize / bitmap.height);
        const w = Math.round(bitmap.width * scale);
        const h = Math.round(bitmap.height * scale);

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
        bitmap.close();

        const { blobUrl, dataUrl } = await canvasToBlobAndDataUrl(canvas);
        const encrypted = await enc.encryptStrWithHash(dataUrl);

        props.field.photos.push({
          key: Date.now(),
          image: { blobUrl, encryptedImgData: encrypted }
        });
      } catch (err) {
        // 壊れた写真エントリは push しない (半端な状態を作らない)。
        console.error('[PhotoField] 画像の取り込みに失敗しました:', err);
        window.alert('画像を取り込めませんでした。別の画像でお試しください。');
      } finally {
        input.value = '';
      }
    }

    function removePhoto(idx) {
      const photo = props.field.photos[idx];
      if (photo?.image?.blobUrl) URL.revokeObjectURL(photo.image.blobUrl);
      props.field.photos.splice(idx, 1);
    }

    async function rotatePhoto(idx) {
      const enc = window.encryption;
      const cur = props.field.photos[idx].image.blobUrl;
      const img = await loadImage(cur);

      const canvas = document.createElement('canvas');
      canvas.width = img.height;
      canvas.height = img.width;
      const ctx = canvas.getContext('2d');
      ctx.rotate(Math.PI / 2);
      ctx.translate(0, -img.height);
      ctx.drawImage(img, 0, 0);

      const { blobUrl, dataUrl } = await canvasToBlobAndDataUrl(canvas);
      URL.revokeObjectURL(cur); // 古い blobUrl 解放

      props.field.photos[idx].image.blobUrl = blobUrl;
      props.field.photos[idx].image.encryptedImgData = await enc.encryptStrWithHash(dataUrl);
    }

    // コンポーネント破棄時、残存写真の Blob URL を解放してリークを防ぐ
    // (削除・回転時は個別に revoke 済みだが、写真が残ったまま破棄される経路への保険)。
    Vue.onBeforeUnmount(() => {
      for (const photo of props.field.photos) {
        if (photo?.image?.blobUrl) URL.revokeObjectURL(photo.image.blobUrl);
      }
    });

    return { cameraInput, libraryInput, canAddMore, triggerCamera, triggerLibrary, onFileSelected, removePhoto, rotatePhoto };
  }
};
</script>
